import React from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  UploadCloud,
  Trash2,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileCode,
  FileVideo,
  FileAudio,
  FileArchive,
  FileJson,
  Folder,
  MessageSquare,
  Plus,
  CheckSquare,
  Square,
  X,
  ChevronRight,
  ChevronDown,
  Edit2,
  FolderPlus,
  MoreVertical,
  Info,
  Search,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { Document, Folder as FolderType, FolderCreateRequest } from '../types';
import { formatDate } from '../utils/dateUtils';
import { truncateDisplayName } from '../utils/textUtils';
import { getRateLimitFromError, dispatchRateLimitExceeded } from '../utils/rateLimit';

/** Get icon and styles for a document based on filename extension */
function getFileTypeIcon(filename: string): {
  Icon: React.ComponentType<{ className?: string }>;
  bgClass: string;
  iconClass: string;
} {
  const ext = (filename || '').split('.').pop()?.toLowerCase() || '';
  const text = ['pdf', 'doc', 'docx', 'rtf', 'odt', 'txt', 'md', 'odf'];
  const spreadsheet = ['xls', 'xlsx', 'ods', 'csv'];
  const image = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
  const code = ['py', 'js', 'ts', 'tsx', 'jsx', 'html', 'css', 'xml', 'yaml', 'yml', 'sh', 'sql'];
  const video = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
  const audio = ['mp3', 'wav', 'ogg', 'm4a', 'flac'];
  const archive = ['zip', 'tar', 'gz', 'rar', '7z'];

  if (ext === 'pdf') return { Icon: FileText, bgClass: 'bg-red-100', iconClass: 'text-red-600' };
  if (ext === 'json') return { Icon: FileJson, bgClass: 'bg-amber-100', iconClass: 'text-amber-600' };
  if (text.includes(ext)) return { Icon: FileText, bgClass: 'bg-blue-100', iconClass: 'text-blue-600' };
  if (spreadsheet.includes(ext)) return { Icon: FileSpreadsheet, bgClass: 'bg-green-100', iconClass: 'text-green-600' };
  if (image.includes(ext)) return { Icon: FileImage, bgClass: 'bg-purple-100', iconClass: 'text-purple-600' };
  if (code.includes(ext)) return { Icon: FileCode, bgClass: 'bg-gray-100', iconClass: 'text-gray-600' };
  if (video.includes(ext)) return { Icon: FileVideo, bgClass: 'bg-pink-100', iconClass: 'text-pink-600' };
  if (audio.includes(ext)) return { Icon: FileAudio, bgClass: 'bg-indigo-100', iconClass: 'text-indigo-600' };
  if (archive.includes(ext)) return { Icon: FileArchive, bgClass: 'bg-yellow-100', iconClass: 'text-yellow-600' };
  // Default (e.g. pptx, other)
  return { Icon: FileText, bgClass: 'bg-primary-100', iconClass: 'text-primary-600' };
}

const GlobalDocuments: React.FC = () => {
  const [documents, setDocuments] = React.useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = React.useState<Document[]>([]);
  const [globalFolders, setGlobalFolders] = React.useState<FolderType[]>([]);
  const [selectedFolderId, setSelectedFolderId] = React.useState<string | null>(null); // null = show all
  const [expandedFolders, setExpandedFolders] = React.useState<Set<string>>(new Set());
  const [loading, setLoading] = React.useState(true);
  const [foldersLoading, setFoldersLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<string | null>(null);
  const [deletingDocId, setDeletingDocId] = React.useState<string | null>(null);
  const [deleteFolderConfirmId, setDeleteFolderConfirmId] = React.useState<string | null>(null);
  const [deletingFolder, setDeletingFolder] = React.useState(false);
  const [createSubfolderParentId, setCreateSubfolderParentId] = React.useState<string | undefined>(undefined);
  const [folderToDelete, setFolderToDelete] = React.useState<FolderType | null>(null);
  const [showInfoModal, setShowInfoModal] = React.useState(false);
  const [selectedDocIds, setSelectedDocIds] = React.useState<Set<string>>(new Set());
  const [showBulkFolderSelector, setShowBulkFolderSelector] = React.useState(false);
  const [availableFolders, setAvailableFolders] = React.useState<FolderType[]>([]);
  const [selectedFolderIds, setSelectedFolderIds] = React.useState<string[]>([]);
  const [bulkAssociating, setBulkAssociating] = React.useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [newFolderParentId, setNewFolderParentId] = React.useState<string | undefined>(undefined);
  const [creatingFolder, setCreatingFolder] = React.useState(false);
  const [uploadFolderId, setUploadFolderId] = React.useState<string | undefined>(undefined);
  const [showUploadFolderSelector, setShowUploadFolderSelector] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // Documents to display: folder-filtered then search-filtered
  const displayDocuments = React.useMemo(() => {
    if (!searchQuery.trim()) return filteredDocuments;
    const q = searchQuery.toLowerCase();
    return filteredDocuments.filter(
      (doc) =>
        doc.title.toLowerCase().includes(q) ||
        doc.filename.toLowerCase().includes(q) ||
        doc.summary_text?.toLowerCase().includes(q)
    );
  }, [filteredDocuments, searchQuery]);

  // Helper function to get all subfolder IDs (not memoized to avoid dependency issues)
  const getAllSubfolderIds = (folders: FolderType[], parentId: string): string[] => {
    const ids: string[] = [parentId];
    const findSubfolders = (folderList: FolderType[], targetParentId: string) => {
      folderList.forEach(folder => {
        if (folder.parent_id === targetParentId) {
          ids.push(folder.id);
          if (folder.children && folder.children.length > 0) {
            findSubfolders(folder.children, folder.id);
          }
        }
      });
    };
    // First, find the folder in the tree
    const findFolder = (folderList: FolderType[]): FolderType | null => {
      for (const folder of folderList) {
        if (folder.id === parentId) {
          return folder;
        }
        if (folder.children && folder.children.length > 0) {
          const found = findFolder(folder.children);
          if (found) return found;
        }
      }
      return null;
    };
    const targetFolder = findFolder(folders);
    if (targetFolder && targetFolder.children && targetFolder.children.length > 0) {
      findSubfolders(targetFolder.children, parentId);
    }
    return ids;
  };

  // Filter documents by folder - separate effect to avoid circular dependencies
  React.useEffect(() => {
    if (selectedFolderId === null) {
      setFilteredDocuments(documents);
    } else {
      // Filter documents that are associated with this folder or its subfolders
      const folderIds = getAllSubfolderIds(globalFolders, selectedFolderId);
      setFilteredDocuments(documents.filter(doc => 
        doc.folder_ids && doc.folder_ids.some(id => folderIds.includes(id))
      ));
    }
  }, [selectedFolderId, documents, globalFolders]);

  const fetchDocuments = React.useCallback(async () => {
    try {
      setLoading(true);
      const { apiService } = await import('../services/api');
      const docs = await apiService.getDocuments(undefined, undefined, { isGlobal: true });
      setDocuments(docs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load global documents');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies - only fetch on mount

  const fetchGlobalFolders = React.useCallback(async () => {
    try {
      setFoldersLoading(true);
      const { apiService } = await import('../services/api');
      const folders = await apiService.getFolders(false, 'global');
      setGlobalFolders(folders);
      const flatFolders = await apiService.getFolders(true, 'global');
      setAvailableFolders(flatFolders);
    } catch (err) {
      console.error('Failed to load global folders:', err);
      toast.error('Failed to load global folders');
    } finally {
      setFoldersLoading(false);
    }
  }, []); // No dependencies - only fetch on mount

  // Helper to get folder path for display
  const getFolderPath = (folderId: string): string => {
    const findFolder = (folders: FolderType[], targetId: string, path: string[] = []): string[] | null => {
      for (const folder of folders) {
        if (folder.id === targetId) {
          return [...path, folder.name];
        }
        if (folder.children && folder.children.length > 0) {
          const found = findFolder(folder.children, targetId, [...path, folder.name]);
          if (found) return found;
        }
      }
      return null;
    };
    const path = findFolder(globalFolders, folderId);
    return path ? path.join(' / ') : '';
  };

  // Fetch data on mount only
  React.useEffect(() => {
    fetchDocuments();
    fetchGlobalFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount

  const consumeUploadStream = async (stream: ReadableStream<Uint8Array>, fallbackName: string) => {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult: any = null;
    let uploadError: Error | null = null;
    let fileSaved = false;
    let queued = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';

      for (const event of events) {
        if (!event.trim() || !event.startsWith('data: ')) continue;
        const jsonStr = event.slice(6).trim();
        if (!jsonStr || jsonStr === '[DONE]') continue;

        try {
          const parsed = JSON.parse(jsonStr);
          const eventType = parsed.event;
          const data = parsed.data || {};

          if (eventType === 'status') {
            const phase = data.phase;
            // Track when file is saved and queued
            if (phase === 'file_saved') {
              fileSaved = true;
              // Store basic info for immediate display
              finalResult = {
                doc_id: data.doc_id,
                title: data.title || fallbackName,
                status: 'processing',
                chunks: 0,
              };
            } else if (phase === 'queuing') {
              queued = true;
              // Update with queued status
              if (finalResult) {
                finalResult.status = 'queued';
              }
            }
            // If we have the doc_id and file is saved + queued, we can return early
            // Processing will continue in background
            if (fileSaved && queued && data.doc_id) {
              // Return immediately - processing continues in background
              reader.releaseLock();
              return finalResult || {
                doc_id: data.doc_id,
                title: fallbackName,
                status: 'processing',
                chunks: 0,
              };
            }
          } else if (eventType === 'completed') {
            // If processing completes quickly, use the completed result
            finalResult = data;
            reader.releaseLock();
            return finalResult;
          } else if (eventType === 'error') {
            const detail = data.detail || `Failed to upload "${fallbackName}".`;
            uploadError = new Error(detail);
            reader.releaseLock();
            throw uploadError;
          }
        } catch (parseErr) {
          console.warn('⚠️ Failed to parse global upload stream event:', parseErr);
        }
      }
    }

    reader.releaseLock();

    if (uploadError) {
      throw uploadError;
    }

    // If we got here and have a result, return it (file was saved and queued)
    if (finalResult) {
      return finalResult;
    }

    // Fallback: return basic info if we have doc_id from status events
    return finalResult || null;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      const { apiService } = await import('../services/api');
      // Use uploadFolderId if set (from upload button), otherwise use selectedFolderId, otherwise undefined
      const folderIdToUse = uploadFolderId || selectedFolderId || undefined;
      
      // Upload all files - they'll be enqueued quickly and processed in parallel
      const uploadPromises = Array.from(files).map(async (file) => {
        try {
          const stream = await apiService.uploadDocumentStream(file, undefined, folderIdToUse, { isGlobal: true });
          const result = await consumeUploadStream(stream, file.name);
          const displayName = result?.title || file.name;
          const status = result?.status || 'processing';
          const chunkCount = typeof result?.chunks === 'number' ? result.chunks : undefined;
          const folderName = folderIdToUse 
            ? (() => {
                // Find folder in tree structure
                const findFolder = (folders: FolderType[], id: string): FolderType | null => {
                  for (const f of folders) {
                    if (f.id === id) return f;
                    if (f.children) {
                      const found = findFolder(f.children, id);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                return findFolder(globalFolders, folderIdToUse)?.name || 'selected folder';
              })()
            : null;
          
          // No toast - document appears in UI automatically
          // User can see the document in the list immediately
          return result;
        } catch (err) {
          const { isRateLimit, message: limitMsg } = getRateLimitFromError(err);
          if (isRateLimit) {
            dispatchRateLimitExceeded(limitMsg);
            throw err;
          }
          toast.error(`Failed to upload "${file.name}": ${err instanceof Error ? err.message : 'Unknown error'}`);
          throw err;
        }
      });

      // Wait for all uploads to be enqueued (not processed)
      await Promise.all(uploadPromises);

      // Refresh documents immediately - they'll show with "processing" status
      const docs = await apiService.getDocuments(undefined, undefined, { isGlobal: true });
      setDocuments(docs);
      // Refresh folders to update document counts
      const folders = await apiService.getFolders(false, 'global');
      setGlobalFolders(folders);
      // Reset upload folder selection
      setUploadFolderId(undefined);
    } catch (err) {
      const { isRateLimit } = getRateLimitFromError(err);
      if (!isRateLimit) {
        toast.error(err instanceof Error ? err.message : 'Failed to upload global document');
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getDeleteErrorMessage = (err: unknown): string => {
    if (err && typeof err === 'object' && 'response' in err) {
      const ax = err as { response?: { status?: number; data?: { detail?: string; message?: string } } };
      if (ax.response?.status === 404) {
        return 'Document not found or you don\'t have permission to delete it.';
      }
      const detail = ax.response?.data?.detail ?? ax.response?.data?.message;
      if (typeof detail === 'string') return detail;
      if (typeof detail === 'object' && detail && 'msg' in detail) return String((detail as { msg: string }).msg);
    }
    return err instanceof Error ? err.message : 'Failed to delete document';
  };

  const handleDelete = async (docId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (deleteConfirmId !== docId) {
      setDeleteConfirmId(docId);
      return;
    }
    setDeletingDocId(docId);
    try {
      const { apiService } = await import('../services/api');
      await apiService.deleteDocument(docId);
      toast.success('Document deleted successfully');
      const docs = await apiService.getDocuments(undefined, undefined, { isGlobal: true });
      setDocuments(docs);
      setDeleteConfirmId(null);
    } catch (err) {
      toast.error(getDeleteErrorMessage(err));
    } finally {
      setDeletingDocId(null);
    }
  };

  const cancelDelete = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDeleteConfirmId(null);
  };

  const formatDateLocal = (dateString: string) =>
    formatDate(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  // Bulk selection handlers
  const toggleDocumentSelection = (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDocIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedDocIds.size === displayDocuments.length) {
      setSelectedDocIds(new Set());
    } else {
      setSelectedDocIds(new Set(displayDocuments.map(doc => doc.doc_id)));
    }
  };

  // Fetch folders when bulk selector opens (only global folders)
  React.useEffect(() => {
    const fetchFolders = async () => {
      if (!showBulkFolderSelector) return;
      
      try {
        const { apiService } = await import('../services/api');
        const folders = await apiService.getFolders(true, 'global');
        setAvailableFolders(folders);
        setSelectedFolderIds([]);
      } catch (err) {
        toast.error('Failed to load folders');
        console.error(err);
      }
    };

    fetchFolders();
  }, [showBulkFolderSelector]);

  const handleBulkAssociateFolders = async () => {
    if (selectedDocIds.size === 0 || selectedFolderIds.length === 0) {
      toast('Please select documents and folders', { icon: 'ℹ️' });
      return;
    }

    try {
      setBulkAssociating(true);
      const { apiService } = await import('../services/api');
      const docIdsArray = Array.from(selectedDocIds);
      
      const result = await apiService.bulkAssociateDocumentsWithFolders(docIdsArray, selectedFolderIds);
      
      if (result.success_count > 0) {
        toast.success(`Successfully associated ${result.success_count} document(s) with folder(s)`);
        if (result.failed_count > 0) {
          toast.error(`${result.failed_count} document(s) failed to associate`);
        }
        
        // Refresh documents
        const { apiService } = await import('../services/api');
        const docs = await apiService.getDocuments(undefined, undefined, { isGlobal: true });
        setDocuments(docs);
        setSelectedDocIds(new Set());
        setShowBulkFolderSelector(false);
      } else {
        toast.error('Failed to associate documents');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to associate documents';
      toast.error(message);
    } finally {
      setBulkAssociating(false);
    }
  };

  const toggleFolderSelection = (folderId: string) => {
    setSelectedFolderIds(prev => {
      if (prev.includes(folderId)) {
        return prev.filter(id => id !== folderId);
      } else {
        return [...prev, folderId];
      }
    });
  };

  const toggleFolderExpansion = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Folder name is required');
      return;
    }

    try {
      setCreatingFolder(true);
      const { apiService } = await import('../services/api');
      const folderData: FolderCreateRequest = {
        name: newFolderName.trim(),
        parent_id: newFolderParentId || createSubfolderParentId,
        folder_type: 'global',
      };
      await apiService.createFolder(folderData);
      toast.success('Global folder created successfully');
      setShowCreateFolderModal(false);
      setNewFolderName('');
      setNewFolderParentId(undefined);
      setCreateSubfolderParentId(undefined);
      // Refresh folders
      const folders = await apiService.getFolders(false, 'global');
      setGlobalFolders(folders);
      const flatFolders = await apiService.getFolders(true, 'global');
      setAvailableFolders(flatFolders);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleCreateSubfolder = (parentId: string) => {
    setCreateSubfolderParentId(parentId);
    setNewFolderParentId(parentId);
    setShowCreateFolderModal(true);
  };

  const handleDeleteFolderClick = (folder: FolderType) => {
    setFolderToDelete(folder);
  };

  const handleDeleteFolderConfirm = async () => {
    if (!folderToDelete) return;

    try {
      setDeletingFolder(true);
      const { apiService } = await import('../services/api');
      await apiService.deleteFolder(folderToDelete.id, false);
      toast.success('Folder deleted successfully');
      setFolderToDelete(null);
      setDeleteFolderConfirmId(null);
      // Refresh folders
      const folders = await apiService.getFolders(false, 'global');
      setGlobalFolders(folders);
      const flatFolders = await apiService.getFolders(true, 'global');
      setAvailableFolders(flatFolders);
      // If deleted folder was selected, clear selection
      if (selectedFolderId === folderToDelete.id) {
        setSelectedFolderId(null);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete folder');
    } finally {
      setDeletingFolder(false);
    }
  };

  const handleUploadToFolder = (folderId: string) => {
    setUploadFolderId(folderId);
    fileInputRef.current?.click();
  };

  const renderFolderTree = (folders: FolderType[], level: number = 0): React.ReactNode => {
    return folders.map(folder => {
      const hasChildren = folder.children && folder.children.length > 0;
      const isExpanded = expandedFolders.has(folder.id);
      const isSelected = selectedFolderId === folder.id;

      return (
        <div key={folder.id} className="group">
          <div
            className={`flex items-center py-2 px-2 rounded hover:bg-gray-100 cursor-pointer ${
              isSelected ? 'bg-primary-50 text-primary-700' : ''
            }`}
            style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
            onClick={() => setSelectedFolderId(folder.id)}
          >
            {hasChildren ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolderExpansion(folder.id);
                }}
                className="mr-1 p-1 hover:bg-gray-200 rounded"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <span className="w-6" />
            )}
            <Folder className="h-4 w-4 mr-2 text-gray-500 flex-shrink-0" />
            <span className="flex-1 text-sm truncate">{folder.name}</span>
            {folder.document_count !== undefined && folder.document_count > 0 && (
              <span className="text-xs text-gray-500 ml-2">({folder.document_count})</span>
            )}
            
            {/* Action buttons - show on hover */}
            <div className="flex items-center ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleUploadToFolder(folder.id);
                }}
                className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                title="Upload to this folder"
              >
                <UploadCloud className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateSubfolder(folder.id);
                }}
                className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                title="Create subfolder"
              >
                <FolderPlus className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteFolderClick(folder);
                }}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
                title="Delete folder"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          {hasChildren && isExpanded && (
            <div>
              {renderFolderTree(folder.children || [], level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-red-600 mb-4">{error}</p>
        <Link to="/" className="text-primary-600 hover:underline">
          Go back
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col w-full min-w-0 overflow-hidden">
      <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center space-x-2">
            <Globe className="h-6 w-6 text-primary-600" />
            <span>Global Documents</span>
            <button
              onClick={() => setShowInfoModal(true)}
              className="ml-2 p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors"
              title="Learn about Global Documents"
            >
              <Info className="h-5 w-5" />
            </button>
          </h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setCreateSubfolderParentId(undefined);
                setNewFolderParentId(undefined);
                setShowCreateFolderModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              <FolderPlus className="h-4 w-4 mr-2" />
              New Folder
            </button>
            <div className="relative">
              <button
                onClick={() => {
                  if (selectedFolderId) {
                    // If a folder is selected, upload directly to that folder
                    fileInputRef.current?.click();
                  } else {
                    // Otherwise, show folder selector
                    setShowUploadFolderSelector(true);
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-70"
                disabled={uploading}
              >
                <UploadCloud className="h-4 w-4 mr-2" />
                {uploading ? 'Uploading...' : selectedFolderId ? 'Upload to Folder' : 'Upload Global Document'}
              </button>
              {selectedFolderId && (
                <span className="absolute -top-2 -right-2 bg-primary-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  ✓
                </span>
              )}
            </div>
          </div>
        </div>
        <p className="text-gray-600 mt-2">
          {documents.length === 0
            ? 'No global documents available yet. Upload shared resources here.'
            : `${documents.length} global document${documents.length !== 1 ? 's' : ''} accessible from any folder.`}
        </p>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          multiple
          onChange={handleFileUpload}
        />
      </div>

      <div className="flex-1 flex overflow-hidden min-h-0 min-w-0">
        {/* Sidebar with folders */}
        <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Global Folders</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {foldersLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <>
                <div
                  className={`group flex items-center py-2 px-2 rounded hover:bg-gray-100 cursor-pointer mb-1 ${
                    selectedFolderId === null ? 'bg-primary-50 text-primary-700' : ''
                  }`}
                  onClick={() => setSelectedFolderId(null)}
                >
                  <Globe className="h-4 w-4 mr-2 text-gray-500 flex-shrink-0" />
                  <span className="flex-1 text-sm font-medium">All Documents</span>
                  <span className="text-xs text-gray-500">({documents.length})</span>
                  <div className="flex items-center ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadFolderId(undefined);
                        fileInputRef.current?.click();
                      }}
                      className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                      title="Upload document"
                    >
                      <UploadCloud className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCreateSubfolderParentId(undefined);
                        setNewFolderParentId(undefined);
                        setShowCreateFolderModal(true);
                      }}
                      className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                      title="Create root folder"
                    >
                      <FolderPlus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {globalFolders.length > 0 ? (
                  renderFolderTree(globalFolders)
                ) : (
                  <p className="text-sm text-gray-500 py-4 text-center">No folders yet</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Main content area - no horizontal scroll so header stays fixed */}
        <div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {/* Search and action bar - always visible when not loading */}
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-gray-200">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by title, filename, or content..."
                    className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900"
                  >
                    {selectedDocIds.size === displayDocuments.length ? (
                      <CheckSquare className="h-5 w-5 text-primary-600" />
                    ) : (
                      <Square className="h-5 w-5 text-gray-400" />
                    )}
                    <span>
                      {selectedDocIds.size === displayDocuments.length 
                        ? 'Deselect All' 
                        : 'Select All'}
                    </span>
                  </button>
                  {selectedDocIds.size > 0 && (
                    <button
                      onClick={() => setShowBulkFolderSelector(true)}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100"
                    >
                      <Folder className="h-4 w-4 mr-1" />
                      Associate with Folders ({selectedDocIds.size})
                    </button>
                  )}
                </div>
              </div>

              {displayDocuments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Globe className="h-16 w-16 text-gray-300 mb-4" />
                  <p className="text-gray-600 text-lg font-medium">
                    {searchQuery.trim()
                      ? 'No documents match your search'
                      : selectedFolderId
                      ? 'No documents in this folder'
                      : 'No global documents yet'}
                  </p>
                  <p className="text-gray-500 mt-2">
                    {searchQuery.trim()
                      ? 'Try a different search term or clear the search.'
                      : selectedFolderId
                      ? 'Upload documents and associate them with this folder.'
                      : 'Upload shared documents here to make them available everywhere.'}
                  </p>
                </div>
              ) : (
              <div className={`divide-y divide-gray-200 border rounded-lg overflow-hidden bg-white min-w-0 w-full max-w-full ${displayDocuments.some((d) => selectedDocIds.has(d.doc_id)) ? 'border-primary-500' : 'border-gray-200'}`}>
                {displayDocuments.map((doc) => {
                  const isSelected = selectedDocIds.has(doc.doc_id);
                  const displayStatus = doc.status || doc.processing_status || 'unknown';
                  const isReady = displayStatus === 'ready' || displayStatus === 'completed';
                  const isProcessing = ['processing', 'queued', 'pending', 'indexing', 'uploaded'].includes(displayStatus);
                  const isFailed = displayStatus === 'failed' || displayStatus === 'error';
                  const isEmpty = displayStatus === 'empty';
                  const { Icon, bgClass, iconClass } = getFileTypeIcon(doc.filename);
                  const statusLabel = isReady ? 'Ready' : isProcessing ? (displayStatus === 'processing' ? 'Processing' : displayStatus === 'queued' ? 'Queued' : displayStatus) : isFailed ? 'Failed' : isEmpty ? 'Empty' : displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1);
                  return (
                    <div
                      key={doc.id}
                      className={`py-2.5 px-3 flex items-center gap-3 hover:bg-gray-50 transition-colors relative group ${isSelected ? 'bg-primary-50' : ''}`}
                    >
                      <div className="flex-shrink-0">
                        <button
                          type="button"
                          onClick={(e) => toggleDocumentSelection(doc.doc_id, e)}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {isSelected ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5 text-gray-400" />}
                        </button>
                      </div>
                      <div
                        className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                        onClick={() => window.open(`/document/${doc.doc_id}`, '_blank')}
                      >
                        <div className={`w-10 h-10 flex-shrink-0 ${bgClass} rounded-lg flex items-center justify-center`}>
                          <Icon className={`h-5 w-5 ${iconClass}`} />
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <p className="text-sm font-medium text-gray-900 truncate" title={doc.title || doc.filename || ''}>
                            {truncateDisplayName(doc.title || doc.filename)}
                            <span className="ml-2 text-gray-500 font-normal">· {doc.chunks} chunks</span>
                            <span className="ml-2 text-gray-500 font-normal">· {formatDateLocal(doc.created_at)}</span>
                            {(doc.status || doc.processing_status) && (
                              <span className="ml-2">
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                    isReady ? 'bg-green-100 text-green-800' : isProcessing ? 'bg-yellow-100 text-yellow-800' : isFailed ? 'bg-red-100 text-red-800' : isEmpty ? 'bg-gray-100 text-gray-800' : 'bg-blue-100 text-blue-800'
                                  }`}
                                  title={`Status: ${displayStatus}`}
                                >
                                  {statusLabel}
                                </span>
                              </span>
                            )}
                          </p>
                          {doc.summary_text && (
                            <p className="text-xs text-gray-500 truncate mt-0.5" title={doc.summary_text}>
                              {(doc.summary_text || '').length > 75 ? `${(doc.summary_text || '').slice(0, 75)}…` : doc.summary_text}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        {deleteConfirmId === doc.doc_id ? (
                          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                            <span className="text-xs text-red-700 font-medium">
                              {deletingDocId === doc.doc_id ? 'Deleting…' : 'Delete?'}
                            </span>
                            {deletingDocId !== doc.doc_id && (
                              <>
                                <button type="button" onClick={(e) => handleDelete(doc.doc_id, e)} className="px-2 py-0.5 bg-red-600 text-white text-xs rounded hover:bg-red-700">Yes</button>
                                <button type="button" onClick={(e) => cancelDelete(e)} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300">No</button>
                              </>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => handleDelete(doc.doc_id, e)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete document"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Upload Folder Selector Modal */}
      {showUploadFolderSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Select Folder for Upload</h2>
              <p className="text-sm text-gray-600 mt-1">
                Choose a folder to organize this document (optional - document will still be accessible to everyone)
              </p>
            </div>
            <div className="px-6 py-4 max-h-96 overflow-y-auto">
              <div className="space-y-2">
                <label
                  className={`flex items-center px-3 py-2 rounded cursor-pointer hover:bg-gray-50 ${
                    !uploadFolderId ? 'bg-primary-50' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="uploadFolder"
                    checked={!uploadFolderId}
                    onChange={() => setUploadFolderId(undefined)}
                    className="mr-3 h-4 w-4 text-primary-600"
                  />
                  <span className="text-sm text-gray-900 flex-1">No Folder (Root Level)</span>
                </label>
                {availableFolders.length > 0 ? (
                  <>
                    {availableFolders.map(folder => {
                      const folderPath = getFolderPath(folder.id);
                      return (
                        <label
                          key={folder.id}
                          className={`flex items-center px-3 py-2 rounded cursor-pointer hover:bg-gray-50 ${
                            uploadFolderId === folder.id ? 'bg-primary-50' : ''
                          }`}
                        >
                          <input
                            type="radio"
                            name="uploadFolder"
                            checked={uploadFolderId === folder.id}
                            onChange={() => setUploadFolderId(folder.id)}
                            className="mr-3 h-4 w-4 text-primary-600"
                          />
                          <Folder className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="text-sm text-gray-900 flex-1">
                            {folderPath || folder.name}
                          </span>
                        </label>
                      );
                    })}
                  </>
                ) : (
                  <p className="text-sm text-gray-500 py-4 text-center">No folders available. Create one first.</p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUploadFolderSelector(false);
                  setUploadFolderId(undefined);
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUploadFolderSelector(false);
                  fileInputRef.current?.click();
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                <UploadCloud className="h-4 w-4 mr-1" />
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {createSubfolderParentId ? 'Create Subfolder' : 'Create Global Folder'}
              </h2>
            </div>
            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter folder name"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newFolderName.trim() && !creatingFolder) {
                      handleCreateFolder();
                    }
                  }}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parent Folder {createSubfolderParentId ? '(Pre-selected)' : '(Optional)'}
                </label>
                <select
                  value={newFolderParentId || createSubfolderParentId || ''}
                  onChange={(e) => {
                    setNewFolderParentId(e.target.value || undefined);
                    setCreateSubfolderParentId(e.target.value || undefined);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={!!createSubfolderParentId}
                >
                  <option value="">None (Root Level)</option>
                  {globalFolders.length > 0 && (
                    <>
                      {globalFolders.map(folder => (
                        <option key={folder.id} value={folder.id}>{folder.name}</option>
                      ))}
                    </>
                  )}
                </select>
                {createSubfolderParentId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Creating subfolder under: {getFolderPath(createSubfolderParentId) || 'selected folder'}
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setNewFolderName('');
                  setNewFolderParentId(undefined);
                  setCreateSubfolderParentId(undefined);
                }}
                disabled={creatingFolder}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={creatingFolder || !newFolderName.trim()}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <FolderPlus className="h-4 w-4 mr-1" />
                {creatingFolder ? 'Creating...' : 'Create Folder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Confirmation Modal */}
      {folderToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-red-600">Delete Folder</h2>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="h-5 w-5 text-red-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    Are you sure you want to delete "{folderToDelete.name}"?
                  </p>
                  <p className="text-sm text-gray-600 mb-3">
                    This action cannot be undone. All documents in this folder will remain, but the folder structure will be removed.
                  </p>
                  {folderToDelete.document_count !== undefined && folderToDelete.document_count > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
                      <p className="text-sm text-yellow-800">
                        <strong>Warning:</strong> This folder contains {folderToDelete.document_count} document(s). 
                        The documents will not be deleted, but they will no longer be associated with this folder.
                      </p>
                    </div>
                  )}
                  {folderToDelete.children && folderToDelete.children.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <p className="text-sm text-yellow-800">
                        <strong>Warning:</strong> This folder has {folderToDelete.children.length} subfolder(s). 
                        All subfolders will also be deleted.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setFolderToDelete(null);
                  setDeleteFolderConfirmId(null);
                }}
                disabled={deletingFolder}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </button>
              <button
                onClick={handleDeleteFolderConfirm}
                disabled={deletingFolder}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {deletingFolder ? 'Deleting...' : 'Delete Folder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Documents Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <Globe className="h-6 w-6 text-primary-600" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">What are Global Documents?</h2>
                </div>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="space-y-6">
                {/* Definition */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <Info className="h-5 w-5 mr-2 text-primary-600" />
                    Definition
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Global Documents</strong> are shared resources that are accessible from <strong>any folder or thread</strong> in your workspace. 
                    Unlike regular documents that are tied to specific folders, global documents are available everywhere, making them perfect for 
                    reference materials, company policies, shared knowledge bases, and frequently accessed information.
                  </p>
                </div>

                {/* How It Works */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <Globe className="h-5 w-5 mr-2 text-primary-600" />
                    How It Works
                  </h3>
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-3">
                    <p className="text-gray-700 leading-relaxed">
                      When you upload a document to <strong>Global Documents</strong>, it becomes part of a shared knowledge pool. 
                      This means that whenever you search or ask questions in <strong>any thread or folder</strong>, the system will 
                      automatically include these global documents in the search results.
                    </p>
                  </div>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
                    <li>Upload documents once, access them from anywhere</li>
                    <li>No need to duplicate documents across folders</li>
                    <li>Automatically included in all searches and queries</li>
                    <li>Organize with folders for better management</li>
                  </ul>
                </div>

                {/* Search Impact */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <MessageSquare className="h-5 w-5 mr-2 text-primary-600" />
                    Impact on Search & Queries
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-1">When you ask a question in any thread:</p>
                      <p className="text-gray-700 text-sm">
                        The system searches through:
                      </p>
                      <ol className="list-decimal list-inside space-y-1 text-gray-700 text-sm ml-2 mt-2">
                        <li><strong>Thread-specific documents</strong> (documents attached to that thread)</li>
                        <li><strong>Folder documents</strong> (documents in the thread's folder and parent folders)</li>
                        <li><strong>Global Documents</strong> (always included, regardless of folder)</li>
                      </ol>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm text-gray-700">
                        <strong className="text-primary-600">Example:</strong> If you have a company policy document in Global Documents, 
                        it will be available when you ask questions in any thread, even if that thread is in a completely different folder.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Benefits */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <CheckSquare className="h-5 w-5 mr-2 text-green-600" />
                    Key Benefits
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900 mb-2">✨ Centralized Knowledge</h4>
                      <p className="text-sm text-green-800">
                        Store important reference materials in one place. No need to copy documents across multiple folders.
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">🔍 Always Available</h4>
                      <p className="text-sm text-blue-800">
                        Global documents are automatically included in every search, ensuring consistent access to shared information.
                      </p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-900 mb-2">📁 Organized Structure</h4>
                      <p className="text-sm text-purple-800">
                        Use folders to organize global documents while maintaining their global accessibility.
                      </p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <h4 className="font-semibold text-orange-900 mb-2">⚡ Efficient Workflow</h4>
                      <p className="text-sm text-orange-800">
                        Upload once, use everywhere. Perfect for company policies, templates, and shared resources.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Use Cases */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <Folder className="h-5 w-5 mr-2 text-primary-600" />
                    Best Use Cases
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-primary-600 text-xs font-semibold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Company Policies & Procedures</p>
                        <p className="text-sm text-gray-600">HR policies, code of conduct, standard operating procedures</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-primary-600 text-xs font-semibold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Reference Materials</p>
                        <p className="text-sm text-gray-600">Technical documentation, API references, knowledge bases</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-primary-600 text-xs font-semibold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Templates & Standards</p>
                        <p className="text-sm text-gray-600">Document templates, style guides, formatting standards</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-primary-600 text-xs font-semibold">4</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Shared Resources</p>
                        <p className="text-sm text-gray-600">Training materials, FAQs, common reference documents</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Important Note */}
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <div className="flex items-start">
                    <Info className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-yellow-900 mb-1">Important Note</p>
                      <p className="text-sm text-yellow-800">
                        Global Documents are <strong>shared across all users</strong> in your workspace. 
                        Use them for information that should be accessible to everyone. For private or folder-specific documents, 
                        upload them directly to the relevant folder instead.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
              <button
                onClick={() => setShowInfoModal(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Folder Selector Modal */}
      {showBulkFolderSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Associate {selectedDocIds.size} Document(s) with Folders
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Select one or more global folders to associate with the selected documents
              </p>
            </div>
            
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {availableFolders.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No folders available</p>
              ) : (
                <div className="space-y-2">
                  {availableFolders.map((folder) => {
                    const isSelected = selectedFolderIds.includes(folder.id);
                    return (
                      <label
                        key={folder.id}
                        className={`flex items-center px-3 py-2 rounded cursor-pointer hover:bg-gray-50 ${
                          isSelected ? 'bg-primary-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleFolderSelection(folder.id)}
                          className="mr-3 h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-900 flex-1">{folder.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkFolderSelector(false);
                  setSelectedFolderIds([]);
                }}
                disabled={bulkAssociating}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </button>
              <button
                onClick={handleBulkAssociateFolders}
                disabled={bulkAssociating || selectedFolderIds.length === 0}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Plus className="h-4 w-4 mr-1" />
                {bulkAssociating ? 'Associating...' : `Associate with ${selectedFolderIds.length} Folder(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalDocuments;
